import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import GlobalStyles from '@mui/material/GlobalStyles';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PrintIcon from '@mui/icons-material/Print';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import LabelObjectRenderer from '@/components/labelDesigner/LabelObjectRenderer';
import { useLiveWeight, printRawViaBridge } from '@/hooks/useLiveWeight';
import { buildTsplLabel } from '@/lib/tspl';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import type { LabelTemplate } from '@/types/labelTemplate';
import type { PrintWeightProductionEntryRequest, WeightProductionEntryDto, WeightProductionPrintHistoryDto } from '@/types/weightProduction';

const PRINT_SCALE = 3.7795; // px per mm at 96dpi, same as every other label print screen

function buildSampleData(entry: WeightProductionEntryDto): Record<string, string> {
  return {
    BarcodeNo: entry.barcodeNo,
    OrderNumber: entry.orderNumber ?? '',
    MaterialCode: entry.materialCode,
    MaterialName: entry.materialName,
    StandardWeight: String(entry.standardWeight),
    MinWeight: String(entry.minWeight),
    MaxWeight: String(entry.maxWeight),
    ActualWeight: String(entry.actualWeight),
    WeightUom: entry.weightUom,
    ToleranceStatus: entry.toleranceStatus,
    WarehouseName: entry.warehouseName,
    HardwareStationName: entry.hardwareStationName ?? '',
    OperatorName: entry.operatorUserName,
    BatchNo: entry.batchNo ?? '',
    ProductionDate: entry.createdDate,
    PrintDate: new Date().toLocaleDateString(),
    PrintTime: new Date().toLocaleTimeString(),
  };
}

/**
 * REPRINT ONLY — the first label for a weighing always comes out of the Kiosk automatically, the
 * moment the captured weight meets the item's tolerance (see WeightProductionKioskPage's autoPrint).
 * There is deliberately no "print" path here: a weighing that has never printed doesn't exist yet
 * as far as this screen is concerned, and offering one would let an operator print a label without
 * the physical unit ever having been on the scale. This screen exists purely for the damaged/lost/
 * jammed-label case, and always re-prints the entry's ALREADY-reserved BarcodeNo — never a new one.
 */
export default function WeightProductionLabelPrintPage() {
  const { t } = useTranslation('weightProduction');
  const { formatDateTime } = useDateFormat();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const { data: entries = [] } = useQuery({
    queryKey: ['weight-production-entries'],
    queryFn: async () => (await apiClient.get<WeightProductionEntryDto[]>('/weight-production-entries')).data,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
  });

  const [entryId, setEntryId] = useState(searchParams.get('entryId') ?? '');
  const [reprintReason, setReprintReason] = useState('');
  const [reprintError, setReprintError] = useState<string | null>(null);
  const { agentAvailable, status: bridgeStatus } = useLiveWeight();
  const [printJob, setPrintJob] = useState<{ entry: WeightProductionEntryDto; template: LabelTemplate } | null>(null);

  const selectedEntry = entries.find((e) => e.id === entryId);

  const { data: history = [], isLoading } = useQuery({
    queryKey: ['weight-production-print-history', entryId],
    queryFn: async () => (await apiClient.get<WeightProductionPrintHistoryDto[]>(`/weight-production-printing/history/${entryId}`)).data,
    enabled: Boolean(entryId),
  });

  const reprintMutation = useMutation({
    mutationFn: async (row: WeightProductionPrintHistoryDto) => {
      const template = templates.find((tpl) => tpl.id === row.labelTemplateId);
      if (!selectedEntry || !template) return;

      const request: PrintWeightProductionEntryRequest = {
        weightProductionEntryId: selectedEntry.id,
        labelTemplateId: row.labelTemplateId,
        isReprint: true,
        reprintReason,
      };
      await apiClient.post('/weight-production-printing', request);

      // Prefer the station's Hardware Bridge: raw TSPL straight to the print head, no dialog, and
      // barcodes encoded by the printer instead of resampled from a browser bitmap. The browser's
      // print dialog is the fallback only where no Bridge is running at all.
      if (agentAvailable) {
        const { commands } = buildTsplLabel(template, buildSampleData(selectedEntry));
        await printRawViaBridge(commands, undefined, `Weight label ${selectedEntry.barcodeNo}`);
        return;
      }
      setPrintJob({ entry: selectedEntry, template });
      window.setTimeout(() => window.print(), 150);
    },
    onError: (error) => setReprintError(error instanceof Error ? error.message : String(error)),
    onSuccess: () => {
      setReprintError(null);
      queryClient.invalidateQueries({ queryKey: ['weight-production-print-history'] });
    },
  });

  const historyColumns: DataTableColumn<WeightProductionPrintHistoryDto>[] = [
    { field: 'printedDate', headerName: t('printing.printedDate'), render: (r) => formatDateTime(r.printedDate) },
    { field: 'labelTemplateName', headerName: t('printing.template') },
    { field: 'printer', headerName: t('printing.printer'), render: (r) => r.printer ?? '—' },
    { field: 'printedByUserName', headerName: t('printing.printedBy') },
    {
      field: 'isReprint',
      headerName: t('printing.reprintColumn'),
      render: (r) => (r.isReprint ? `${t('printing.reprint')}${r.reprintReason ? ` — ${r.reprintReason}` : ''}` : '—'),
    },
    {
      field: 'actions', headerName: '', align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_EXECUTE}>
          <Button
            size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />}
            disabled={!reprintReason || reprintMutation.isPending}
            onClick={() => reprintMutation.mutate(r)}
          >
            {t('printing.reprint')}
          </Button>
        </IfPermitted>
      ),
    },
  ];

  const printSampleData = printJob ? buildSampleData(printJob.entry) : {};

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#label-print-area, #label-print-area *': { visibility: 'visible' },
            '#label-print-area': { position: 'absolute', top: 0, left: 0, margin: 0 },
          },
          '@page': { size: printJob ? `${printJob.template.widthMm}mm ${printJob.template.heightMm}mm` : 'auto', margin: 0 },
        }}
      />

      <PageHeader title={t('printing.title')} subtitle={t('printing.subtitle')} />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('printing.reprintOnlyNotice')}
      </Alert>
      <Alert severity={agentAvailable ? 'success' : 'warning'} sx={{ mb: 3 }}>
        {agentAvailable
          ? t('printing.directPrintNotice', { printer: bridgeStatus?.printer.name ?? '' })
          : t('printing.browserPrintNotice')}
      </Alert>
      {reprintError && <Alert severity="error" sx={{ mb: 2 }}>{reprintError}</Alert>}

      <Stack spacing={2}>
        <GridContainer columns={12} spacing={2}>
          <GridItem xs={12} md={6}>
            <SearchableSelect
              label={t('printing.entry')} fullWidth value={entryId} onChange={setEntryId}
              options={entries.map((e) => ({ value: e.id, label: `${e.barcodeNo} — ${e.materialCode} (${e.actualWeight} ${e.weightUom})` }))}
            />
          </GridItem>
          <GridItem xs={12} md={6}>
            <TextField
              label={t('printing.reprintReason')} fullWidth required value={reprintReason}
              onChange={(e) => setReprintReason(e.target.value)}
              helperText={t('printing.reprintReasonHelper')}
            />
          </GridItem>
        </GridContainer>

        {entryId ? (
          history.length > 0 || isLoading ? (
            <DataTable columns={historyColumns} rows={history} loading={isLoading} getRowId={(r) => r.id} searchable={false} exportable={false} />
          ) : (
            <Alert severity="warning">{t('printing.neverPrinted')}</Alert>
          )
        ) : (
          <Typography variant="body2" color="text.secondary">{t('printing.selectEntryHint')}</Typography>
        )}
      </Stack>

      {/* Off-screen print area — only visible while actually printing (see @media print above) */}
      {printJob && (
        <Box
          id="label-print-area"
          sx={{
            position: 'fixed', top: '-9999px', left: '-9999px', bgcolor: '#fff',
            width: `${printJob.template.widthMm}mm`, height: `${printJob.template.heightMm}mm`,
          }}
        >
          {printJob.template.objects.map((obj) => (
            <LabelObjectRenderer key={obj.id} object={obj} sampleData={printSampleData} scale={PRINT_SCALE} />
          ))}
        </Box>
      )}
    </>
  );
}
